import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Wind, Target, Sparkles, Play, Pause, Heart, Lock, Crown } from 'lucide-react';
import axios from 'axios';
import Disclaimer from '@/components/Disclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Free exercises (basic breathing and one grounding)
const FREE_EXERCISE_IDS = ['breathing-1', 'breathing-2', 'grounding-1'];

const typeIcons = {
  breathing: Wind,
  grounding: Target,
  release: Sparkles,
  healing: Heart
};

const typeLabels = {
  breathing: 'Andning',
  grounding: 'Förankring',
  release: 'Släppa taget',
  healing: 'Healing'
};

const typeColors = {
  breathing: 'bg-blue-100/50',
  grounding: 'bg-amber-100/50',
  release: 'bg-violet-100/50',
  healing: 'bg-rose-100/50'
};

export default function ExercisesPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [activeExercise, setActiveExercise] = useState(null);
  const [breathPhase, setBreathPhase] = useState('ready');
  const [breathCount, setBreathCount] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const timerRef = useRef(null);

  const c = {
    back: 'Tillbaka',
    allExercises: 'Alla övningar',
    title: 'Övningar',
    breatheIn: 'Andas in...',
    hold: 'Håll...',
    breatheOut: 'Andas ut...',
    ready: 'Redo',
    breath: 'Andetag',
    of: 'av',
    stop: 'Avsluta',
    requiresPremium: 'Denna övning kräver premium',
    couldNotFetch: 'Kunde inte hämta övningar',
    exerciseDone: 'Bra jobbat! Övningen är klar.',
    start: 'Starta',
    duration: 'min',
    premiumRequired: 'Premium',
    all: 'Alla'
  };

  useEffect(() => {
    fetchExercises();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const isExerciseFree = (exercise) => {
    return FREE_EXERCISE_IDS.includes(exercise.id);
  };

  const canAccessExercise = (exercise) => {
    return hasAccess || isExerciseFree(exercise);
  };

  const handleExerciseClick = (exercise) => {
    if (canAccessExercise(exercise)) {
      setSelectedExercise(exercise);
    } else {
      toast.error(c.requiresPremium);
      navigate('/subscription');
    }
  };

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercises`);
      setExercises(response.data);
    } catch (error) {
      toast.error(c.couldNotFetch);
    } finally {
      setLoading(false);
    }
  };

  const startBreathingExercise = (exercise) => {
    setActiveExercise(exercise);
    setBreathPhase('inhale');
    setBreathCount(0);
    
    let phase = 'inhale';
    let count = 0;
    
    timerRef.current = setInterval(() => {
      if (phase === 'inhale') {
        phase = 'hold';
        setBreathPhase('hold');
      } else if (phase === 'hold') {
        phase = 'exhale';
        setBreathPhase('exhale');
      } else if (phase === 'exhale') {
        count++;
        setBreathCount(count);
        if (count >= 5) {
          stopExercise();
          toast.success(c.exerciseDone);
        } else {
          phase = 'inhale';
          setBreathPhase('inhale');
        }
      }
    }, 4000);
  };

  const stopExercise = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setActiveExercise(null);
    setBreathPhase('ready');
    setBreathCount(0);
  };

  const getBreathText = () => {
    switch (breathPhase) {
      case 'inhale': return c.breatheIn;
      case 'hold': return c.hold;
      case 'exhale': return c.breatheOut;
      default: return c.ready;
    }
  };

  const types = [...new Set(exercises.map(e => e.type))];
  const filteredExercises = selectedType 
    ? exercises.filter(e => e.type === selectedType)
    : exercises;

  return (
    <div className="min-h-screen bg-background" data-testid="exercises-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedExercise ? setSelectedExercise(null) : navigate('/category/calm')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedExercise ? c.allExercises : c.back}
          </Button>
          <h1 className="font-heading text-xl font-light">{c.title}</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Active breathing exercise overlay */}
        {activeExercise && (
          <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="breathing-overlay">
            <div className="text-center space-y-8 p-8">
              <div className="relative">
                <div className={`w-48 h-48 mx-auto rounded-full bg-primary/10 flex items-center justify-center transition-transform duration-[4000ms] ease-in-out ${
                  breathPhase === 'inhale' ? 'scale-125' : 
                  breathPhase === 'hold' ? 'scale-125' : 'scale-100'
                }`}>
                  <div className={`w-32 h-32 rounded-full bg-primary/20 transition-transform duration-[4000ms] ease-in-out ${
                    breathPhase === 'inhale' ? 'scale-110' : 
                    breathPhase === 'hold' ? 'scale-110' : 'scale-100'
                  }`} />
                </div>
              </div>
              
              <div className="space-y-2">
                <h2 className="font-heading text-3xl font-light">{getBreathText()}</h2>
                <p className="text-muted-foreground">{c.breath} {breathCount + 1} {c.of} 5</p>
              </div>
              
              <Button
                onClick={stopExercise}
                variant="outline"
                className="rounded-full"
                data-testid="stop-exercise-btn"
              >
                <Pause className="w-4 h-4 mr-2" />
                {c.stop}
              </Button>
            </div>
          </div>
        )}

        {/* Exercise detail view */}
        {selectedExercise ? (
          <div className="animate-fade-in">
            <div className={`${typeColors[selectedExercise.type] || 'bg-secondary'} rounded-2xl p-8 lg:p-12 space-y-8`}>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = typeIcons[selectedExercise.type] || Wind;
                    return (
                      <div className="w-12 h-12 rounded-full bg-card flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                    );
                  })()}
                  <span className="text-sm text-muted-foreground">
                    {typeLabels[selectedExercise.type] || selectedExercise.type} · {selectedExercise.duration_minutes} min
                  </span>
                </div>
                <h2 className="font-heading text-3xl font-light">{selectedExercise.title}</h2>
                <p className="text-muted-foreground leading-relaxed">{selectedExercise.description}</p>
              </div>

              {/* Steps */}
              <div className="space-y-4 bg-card/50 rounded-xl p-6">
                <h3 className="font-heading text-xl">Så här gör du</h3>
                <ol className="space-y-3">
                  {selectedExercise.steps.map((step, index) => (
                    <li key={index} className="flex gap-4 items-start">
                      <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-muted-foreground pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                <h3 className="font-heading text-xl">Fördelar</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedExercise.benefits.map((benefit, index) => (
                    <span key={index} className="px-4 py-2 rounded-full bg-card text-sm">
                      {benefit}
                    </span>
                  ))}
                </div>
              </div>

              {/* Start button for breathing exercises */}
              {selectedExercise.type === 'breathing' && (
                <div className="pt-4">
                  <Button
                    onClick={() => startBreathingExercise(selectedExercise)}
                    className="rounded-full px-8 py-6 text-base bg-primary hover:bg-primary/90"
                    data-testid="start-exercise-btn"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Starta guidad övning
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Intro */}
            <div className="text-center mb-8 space-y-3 animate-fade-in">
              <h2 className="font-heading text-3xl font-light">Övningar & verktyg</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                {exercises.length} övningar för andning, förankring, självmedkänsla och healing.
              </p>
            </div>

            {/* Type filter */}
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              <Button
                variant={selectedType === null ? "default" : "outline"}
                onClick={() => setSelectedType(null)}
                className="rounded-full"
                data-testid="filter-all"
              >
                Alla ({exercises.length})
              </Button>
              {types.map(type => {
                const Icon = typeIcons[type] || Wind;
                return (
                  <Button
                    key={type}
                    variant={selectedType === type ? "default" : "outline"}
                    onClick={() => setSelectedType(type)}
                    className="rounded-full"
                    data-testid={`filter-${type}`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {typeLabels[type] || type}
                  </Button>
                );
              })}
            </div>

            {/* Exercise list */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
              </div>
            ) : (
              <>
                {!hasAccess && (
                  <div className="bg-primary/5 rounded-xl p-4 mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Crown className="w-5 h-5 text-primary" />
                      <span className="text-sm">3 gratis övningar • Uppgradera för alla 15</span>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => navigate('/subscription')}
                      className="rounded-full"
                    >
                      Uppgradera
                    </Button>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredExercises.map((exercise, index) => {
                    const Icon = typeIcons[exercise.type] || Wind;
                    const isFree = isExerciseFree(exercise);
                    const canAccess = canAccessExercise(exercise);
                    return (
                      <button
                        key={exercise.id}
                        onClick={() => handleExerciseClick(exercise)}
                        className={`${typeColors[exercise.type] || 'bg-secondary'} p-6 rounded-2xl text-left transition-shadow duration-300 hover:shadow-soft opacity-0 animate-slide-up group ${!canAccess ? 'opacity-75' : ''}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                        data-testid={`exercise-card-${exercise.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-10 h-10 rounded-full bg-card flex items-center justify-center group-hover:-translate-y-1 transition-transform duration-300">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex items-center gap-2">
                            {!canAccess && <Lock className="w-4 h-4 text-muted-foreground" />}
                            <span className="text-xs text-muted-foreground">
                              {exercise.duration_minutes} min
                            </span>
                          </div>
                        </div>
                        <h3 className="font-heading text-xl font-normal mb-2">{exercise.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">{exercise.description}</p>
                        <div className="mt-4">
                        <span className="text-xs px-3 py-1 rounded-full bg-card/80">
                          {typeLabels[exercise.type] || exercise.type}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              </>
            )}
          </>
        )}

        {/* Disclaimer */}
        <div className="mt-12">
          <Disclaimer variant="mini" />
        </div>
      </main>
    </div>
  );
}
